import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Card } from '../ui/card';
import { 
  UserPlus, Users, Shield, CheckCircle, XCircle, 
  Search, Edit, Filter, ChevronLeft, ChevronRight,
  MoreHorizontal, Mail
} from 'lucide-react';
import useUser from '../../api/user';
import UserFormModal from './modals/UserFormModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { 
  Select,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectItem
} from '../ui/select';
import { Badge } from "../ui/badge";

const UserManagement = () => {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredUsers, setFilteredUsers] = useState([]);
    const userApi = useUser();
    const [users, setUsers] = useState([]);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
    const [showFilters, setShowFilters] = useState(false);

    // Track current filter selections
    const [roleFilter, setRoleFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [usersPerPage, setUsersPerPage] = useState(5);

    // Check for mobile view
    useEffect(() => {
      const handleResize = () => {
        const mobile = window.innerWidth < 768;
        setIsMobileView(mobile);
        // Set fewer items per page on mobile
        setUsersPerPage(mobile ? 3 : 5);
      };
      
      handleResize(); // Set initial values
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
      fetchUsers();
    }, []);

    // Reset to first page when filters change
    useEffect(() => {
      setCurrentPage(1);
      applyAllFilters();
    }, [searchQuery, roleFilter, statusFilter, users]);

    const fetchUsers = async () => {
      try {
        const data = await userApi.getAllUsers();
        setUsers(data);
        setFilteredUsers(data);
      } catch (error) {
        console.error(t('management.users.errors.fetchUsers'), error);
      }
    };

    const applyAllFilters = () => {
      let filtered = users.filter(user => {
        // Apply role filter
        const roleMatches = 
            roleFilter === "all" || 
            user.role == roleFilter; // Using loose equality
        
        // Apply status filter
        const statusMatches = 
            statusFilter === "all" || 
            user.status == statusFilter; // Using loose equality
        
        // Apply search filter if query exists
        const searchMatches = !searchQuery || 
            user.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchQuery.toLowerCase());
        
        // All conditions must be true
        return roleMatches && statusMatches && searchMatches;
      });
    
      setFilteredUsers(filtered);
    };
  
    // Update role filter and apply all filters
    const handleRoleFilterChange = (value) => {
      setRoleFilter(value);
    };

    // Update status filter and apply all filters
    const handleStatusFilterChange = (value) => {
      setStatusFilter(value);
    };

    // Add a new user
    const handleAddUser = async (userData) => {
      try {
        const newUser = await userApi.addUser(userData);
        setUsers([...users, newUser]);
        return newUser;
      } catch (error) {
        console.error(t('management.users.errors.addUser'), error);
        throw error;
      }
    };

    // Update a user
    const handleUpdateUser = async (userData) => {
      try {
        const updatedUser = await userApi.updateUser(userData);
        setUsers(users.map(user => 
                  user.id === updatedUser.id ? updatedUser : user
                ));
        return updatedUser;
      } catch (error) {
        console.error(t('management.users.errors.updateUser'), error);
        throw error;
      }
    };

    // Function to handle modal submission (either add or update)
    const handleSubmitUser = (userData) => {
      if (editingUser) {
        return handleUpdateUser(userData);
      } else {
        return handleAddUser(userData);
      }
    };
    
    // Function to open modal for editing
    const openEditModal = (user) => {
      setEditingUser(user);
      setIsModalOpen(true);
    };

    // Function to open modal for adding
    const openAddModal = () => {
      setEditingUser(null);
      setIsModalOpen(true);
    };

    // Function to close modal
    const closeModal = () => {
      setIsModalOpen(false);
      setEditingUser(null);
    };
    
    // Get current users for pagination
    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
    
    // Calculate total pages
    const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
    
    // Change page
    const nextPage = () => {
      if (currentPage < totalPages) {
        setCurrentPage(currentPage + 1);
      }
    };
    
    const prevPage = () => {
      if (currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    };
    
    // Go to specific page
    const goToPage = (pageNumber) => {
      setCurrentPage(pageNumber);
    };
    
    // Generate page numbers - limit visible pages on mobile
    const getPageNumbers = () => {
      if (isMobileView) {
        // On mobile, show fewer page numbers
        if (totalPages <= 3) {
          return Array.from({ length: totalPages }, (_, i) => i + 1);
        } else {
          // Show current page and one on each side when possible
          if (currentPage === 1) {
            return [1, 2, 3];
          } else if (currentPage === totalPages) {
            return [totalPages - 2, totalPages - 1, totalPages];
          } else {
            return [currentPage - 1, currentPage, currentPage + 1];
          }
        }
      } else {
        // On desktop, show all pages (or up to 5)
        if (totalPages <= 5) {
          return Array.from({ length: totalPages }, (_, i) => i + 1);
        } else {
          // More than 5 pages, show current page and 2 on each side when possible
          const startPage = Math.max(1, currentPage - 2);
          const endPage = Math.min(totalPages, startPage + 4);
          return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
        }
      }
    };

    const pageNumbers = getPageNumbers();

    // Mobile card view component
    const UserCard = ({ user }) => (
      <Card className="mb-3 overflow-hidden">
        <div className="p-4">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h3 className="font-semibold">{user.username}</h3>
              <div className="flex items-center gap-1 mt-1">
                <Mail className="w-3 h-3 text-neutral-500" />
                <span className="text-xs text-neutral-600 dark:text-neutral-400 truncate max-w-[180px]">
                  {user.email}
                </span>
              </div>
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger className="p-2 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800">
                <MoreHorizontal className="h-5 w-5 text-neutral-500" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openEditModal(user)}>
                  <Edit className="w-4 h-4 mr-2" /> {t('common.edit')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          <div className="flex items-center justify-between mt-3">
            <div className="flex gap-2">
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                user.role == '2' 
                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' 
                : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
              }`}>
                {user.role == '2' ? <Shield className="w-3 h-3" /> : <Users className="w-3 h-3" />}
                {user.role == '2' ? t('management.users.roles.manager') : t('management.users.roles.resident')}
              </span>
              
              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                user.status == '1' 
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
              }`}>
                {user.status == '1' ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                {user.status == '1' ? t('management.users.status.active') : t('management.users.status.inactive')}
              </span>
            </div>
            
            <Badge variant="outline" className="font-medium">
              {user.points} {t('profile.balance.available')}
            </Badge>
          </div>
        
        </div>
      </Card>
    );

    return (
      <div className="space-y-4 md:space-y-6 p-2 md:p-4">
        {/* Header with Add Button */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl md:text-2xl font-bold">{t('management.users.title')}</h2>
            <p className="text-sm text-muted-foreground hidden md:block">{t('management.users.description')}</p>
          </div>
        </div>

        {/* Search and Filter Toggle for Mobile */}
        <div className="flex gap-2 md:hidden">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
            <input
              type="text"
              placeholder={t('management.users.searchPlaceholder')}
              className="pl-10 pr-4 py-2 w-full rounded-lg bg-neutral-100 dark:bg-neutral-800 border border-transparent focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg ${showFilters ? 'bg-primary text-white' : 'bg-neutral-100 dark:bg-neutral-800'}`}
          >
            <Filter className="w-5 h-5" />
          </button>
        </div>

        {/* Expandable Filters on Mobile */}
        {(showFilters || !isMobileView) && (
          <Card className={`p-4 bg-white dark:bg-neutral-900 ${isMobileView ? 'animate-in slide-in-from-top duration-300' : ''}`}>
            <div className="flex flex-col md:flex-row justify-between gap-3 md:gap-4">
              {/* Search - Only visible on desktop */}
              <div className="relative flex-1 hidden md:block">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
                <input
                  type="text"
                  placeholder={t('management.users.searchPlaceholder')}
                  className="pl-10 pr-4 py-2 w-full rounded-lg bg-neutral-100 dark:bg-neutral-800 border border-transparent focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              {/* Role Filter */}
              <div className="w-full md:w-48">
                <Select
                  value={roleFilter}
                  onValueChange={handleRoleFilterChange}
                >
                  <SelectTrigger className="bg-neutral-100 dark:bg-neutral-800 w-full">
                    <SelectValue placeholder={t('management.users.filters.allRoles')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('management.users.filters.allRoles')}</SelectItem>
                    <SelectItem value="1">{t('management.users.roles.resident')}</SelectItem>
                    <SelectItem value="2">{t('management.users.roles.manager')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Status Filter */}
              <div className="w-full md:w-48">
                <Select
                  value={statusFilter}
                  onValueChange={handleStatusFilterChange}
                >
                  <SelectTrigger className="bg-neutral-100 dark:bg-neutral-800 w-full">
                    <SelectValue placeholder={t('management.users.filters.allStatus')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('management.users.filters.allStatus')}</SelectItem>
                    <SelectItem value="1">{t('management.users.status.active')}</SelectItem>
                    <SelectItem value="0">{t('management.users.status.inactive')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>
        )}

        {/* Mobile Card View */}
        {isMobileView ? (
          <div className="space-y-1">
            {currentUsers.length > 0 ? (
              currentUsers.map(user => (
                <UserCard key={`mobile-${user.id}`} user={user} />
              ))
            ) : (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground">{t('management.users.noUsers')}</p>
              </Card>
            )}
          </div>
        ) : (
          /* Desktop Table View */
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-neutral-50 dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{t('management.users.table.firstName')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{t('management.users.table.lastName')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{t('management.users.table.username')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{t('management.users.table.email')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{t('management.users.table.points')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{t('management.users.table.role')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{t('management.users.table.status')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{t('management.users.table.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                  {currentUsers.length > 0 ? (
                    currentUsers.map(user => (
                      <tr key={user.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap font-medium">{user.first_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap font-medium">{user.last_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap font-medium">{user.username}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{user.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{user.points}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            user.role == '2' // Using loose equality
                            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' 
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                          }`}>
                            {user.role == '2' ? <Shield className="w-3 h-3" /> : <Users className="w-3 h-3" />}
                            {user.role == '2' ? t('management.users.roles.buildingManager') : t('management.users.roles.resident')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            user.status == '1' // Using loose equality
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          }`}>
                            {user.status == '1' ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                            {user.status == '1' ? t('management.users.status.active') : t('management.users.status.inactive')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex gap-2">
                            <button 
                              className="flex items-center gap-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-2 py-1 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                              onClick={() => openEditModal(user)}
                            >
                              <Edit className="w-3 h-3" />
                              {t('common.edit')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center text-muted-foreground">
                        {t('management.users.noMatchingUsers')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        )}
        
        {/* Pagination Controls - Responsive */}
        {filteredUsers.length > 0 && (
          <Card className="p-3 md:p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-xs md:text-sm text-neutral-500 dark:text-neutral-400 hidden sm:block">
                {t('management.users.pagination.showing', {
                  from: indexOfFirstUser + 1,
                  to: Math.min(indexOfLastUser, filteredUsers.length),
                  total: filteredUsers.length
                })}
              </div>
              
              <div className="flex items-center space-x-1 md:space-x-2">
                <button 
                  onClick={prevPage} 
                  disabled={currentPage === 1}
                  className={`p-1 rounded-md ${currentPage === 1 
                    ? 'text-neutral-300 dark:text-neutral-600 cursor-not-allowed' 
                    : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'}`}
                  aria-label={t('management.users.pagination.previous')}
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                
                {pageNumbers.map(number => (
                  <button
                    key={number}
                    onClick={() => goToPage(number)}
                    className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm ${
                      currentPage === number 
                        ? 'bg-primary text-white' 
                        : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                    }`}
                    aria-label={t('management.users.pagination.pageNumber', { number })}
                  >
                    {number}
                  </button>
                ))}
                
                <button 
                  onClick={nextPage} 
                  disabled={currentPage === totalPages}
                  className={`p-1 rounded-md ${currentPage === totalPages 
                    ? 'text-neutral-300 dark:text-neutral-600 cursor-not-allowed' 
                    : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'}`}
                  aria-label={t('management.users.pagination.next')}
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </Card>
        )}

        <UserFormModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onSubmit={handleSubmitUser}
          user={editingUser}
        />
      </div>
    );
};

export default UserManagement;